/**
 * Format Utility Functions
 *
 * Functions that convert internal App Builder data structures
 * back into simplified AI-friendly format strings.
 * This is the reverse operation of parsing utilities.
 */

import { DefaultVariableResult, VariableSelectFeatureFlag } from '@makyoapp/makyo-common'

import type {
  AppBreakpoint,
  ColorAdaptiveValue,
  ColorType,
  ColorVariant,
  ComponentAlignment,
  GradientDirection,
  HorizontalAlignment,
  ResponsiveValue,
  SpacingInputValue,
  TextAlignment,
  TypographyValue,
  VariableTextValue,
  VerticalAlignment,
} from './types'

export const BREAKPOINTS: AppBreakpoint[] = ['phone', 'phone-landscape', 'tablet', 'tablet-landscape', 'desktop', 'tv']
export const BREAKPOINT_PREFIXES: Record<string, AppBreakpoint> = {
  ph: 'phone',
  pl: 'phone-landscape',
  ta: 'tablet',
  tl: 'tablet-landscape',
  de: 'desktop',
  tv: 'tv',
}

/**
 * Format VariableTextValue back to string array format
 * Converts parsed VariableTextValue back to ["text:Hello", "sc:table:col:row"] format
 */
export function formatFromVariableText(variableText: VariableTextValue | undefined): string[] {
  if (!variableText || !Array.isArray(variableText) || variableText.length === 0) {
    return []
  }

  const result: string[] = []
  for (const node of variableText) {
    if (typeof node === 'object' && node !== null && 'children' in node && Array.isArray(node.children)) {
      for (const child of node.children) {
        if (typeof child === 'object' && child !== null) {
          if ('text' in child && typeof child.text === 'string') {
            // Plain text
            result.push(`text:${child.text}`)
          } else if ('flag' in child) {
            // Variable select - reconstruct format string
            const flag = (child as any).flag
            // ColumnData uses flag 1, ProfileData uses flag 2 (in VariableTextFeatureFlag)
            // Both use col property, but ColumnData is for table columns, ProfileData is for user profile
            if (flag === 1) {
              // ColumnData: cd:<col-id>
              result.push(`cd:${(child as any).col || ''}`)
            } else if (flag === VariableSelectFeatureFlag.CustomText) {
              result.push(`text:${(child as any).value || ''}`)
            } else if (flag === VariableSelectFeatureFlag.Icon) {
              result.push(`icon:${(child as any).value || ''}`)
            } else if (flag === VariableSelectFeatureFlag.ProfileData || flag === 2) {
              // ProfileData: profile:<col-id> (flag 2 in VariableTextFeatureFlag, flag 8 in VariableSelectFeatureFlag)
              result.push(`profile:${(child as any).col || ''}`)
            } else if (flag === VariableSelectFeatureFlag.SingleCell) {
              const sc = child as any
              result.push(`sc:${sc.tbl || ''}:${sc.col || ''}:${sc.row || ''}`)
            } else if (flag === VariableSelectFeatureFlag.CurrentRow) {
              result.push(`cr:${(child as any).col || ''}`)
            } else if (flag === VariableSelectFeatureFlag.Image) {
              result.push(`image:${(child as any).value || ''}`)
            } else if (flag === VariableSelectFeatureFlag.File) {
              result.push(`file:${(child as any).value || ''}`)
            } else if (flag === VariableSelectFeatureFlag.RichText) {
              result.push(`rc:${(child as any).value || ''}`)
            } else if (flag === VariableSelectFeatureFlag.DateTime) {
              result.push(`dt:${(child as any).variant || ''}`)
            } else if (flag === VariableSelectFeatureFlag.GlobalVariable) {
              result.push(`gv:${(child as any).value || ''}`)
            } else if (flag === VariableSelectFeatureFlag.PageCover) {
              result.push('pc')
            } else if (flag === VariableSelectFeatureFlag.PageTitle) {
              result.push('pt')
            } else if (flag === VariableSelectFeatureFlag.CurrentDateTime) {
              result.push('cdt')
            } else if (flag === VariableSelectFeatureFlag.SessionID) {
              result.push('sid')
            } else if (flag === VariableSelectFeatureFlag.UniqueID) {
              result.push('uid')
            }
          }
        }
      }
    }
  }

  return result.length > 0 ? result : ['text:']
}

/**
 * Format VariableSelectValue back to string format
 * Converts DefaultVariableResult back to "icon:home" format
 */
export function formatFromVariableSelect(select: DefaultVariableResult | undefined): string | undefined {
  if (!select) return undefined

  // Handle local images (isLocal: true) - special case: no flag means it's a local image, not a variable select
  // Format: "image:local:<value>" to indicate it's a local image with the path
  if ((select as any).isLocal && !select.flag) {
    const value = (select as any).value || ''
    if (value) {
      return `image:local:${value}`
    }
    return `image:local:`
  }

  // Handle internal images with col reference (isLocal: true) - check this first as it may not have flag
  if ((select as any).isLocal && (select as any).col) {
    // Include the value (path) if present: "image:col:<col-id>:<value>"
    const colId = (select as any).col
    const value = (select as any).value || ''
    if (value) {
      return `image:col:${colId}:${value}`
    }
    return `image:col:${colId}`
  }

  if (!select.flag) return undefined

  const flag = select.flag
  if (flag === VariableSelectFeatureFlag.CustomText) {
    return `text:${select.value || ''}`
  } else if (flag === VariableSelectFeatureFlag.Icon) {
    return `icon:${select.value || ''}`
  } else if (flag === VariableSelectFeatureFlag.ProfileData) {
    return `profile:${select.col || ''}`
  } else if (flag === VariableSelectFeatureFlag.SingleCell) {
    return `sc:${select.tbl || ''}:${select.col || ''}:${select.row || ''}`
  } else if (flag === VariableSelectFeatureFlag.CurrentRow) {
    return `cr:${select.col || ''}`
  } else if (flag === VariableSelectFeatureFlag.Image) {
    // Handle internal images with col reference (isLocal: true)
    if ((select as any).isLocal && (select as any).col) {
      return `image:col:${(select as any).col}`
    }
    return `image:${select.value || ''}`
  } else if (flag === VariableSelectFeatureFlag.File) {
    return `file:${select.value || ''}`
  } else if (flag === VariableSelectFeatureFlag.RichText) {
    return `rc:${select.value || ''}`
  } else if (flag === VariableSelectFeatureFlag.DateTime) {
    return `dt:${select.variant || ''}`
  } else if (flag === VariableSelectFeatureFlag.GlobalVariable) {
    return `gv:${select.value || ''}`
  } else if (flag === VariableSelectFeatureFlag.PageCover) {
    return 'pc'
  } else if (flag === VariableSelectFeatureFlag.PageTitle) {
    return 'pt'
  } else if (flag === VariableSelectFeatureFlag.CurrentDateTime) {
    return 'cdt'
  } else if (flag === VariableSelectFeatureFlag.SessionID) {
    return 'sid'
  } else if (flag === VariableSelectFeatureFlag.UniqueID) {
    return 'uid'
  }

  return undefined
}

/**
 * Format responsive value back to format string
 * Converts ResponsiveValue<T> back to "default ta:value de:value" format
 */
export function formatResponsiveValue<T>(
  value: ResponsiveValue<T> | undefined,
  formatValue: (val: T) => string,
  compareWithDefault?: (val: T) => boolean
): string | undefined {
  if (!value) return undefined

  // Use ordered breakpoints to ensure consistent ordering
  const breakpoints: AppBreakpoint[] = ['phone', 'phone-landscape', 'tablet', 'tablet-landscape', 'desktop', 'tv']
  const values = breakpoints.map((bp) => value[bp]).filter((v) => v !== undefined) as T[]
  const firstValue = values[0]

  if (!firstValue) return undefined

  // Check if all values are the same
  const allSame = values.every((v) => {
    if (compareWithDefault) {
      return compareWithDefault(v) === compareWithDefault(firstValue)
    }
    return JSON.stringify(v) === JSON.stringify(firstValue)
  })

  if (allSame) {
    const formatted = formatValue(firstValue)
    // If compareWithDefault is provided and value matches default, return undefined
    if (compareWithDefault && compareWithDefault(firstValue)) {
      return undefined
    }
    return formatted
  }

  // Build responsive format string
  const parts: string[] = []
  const prefixes: Record<AppBreakpoint, string> = {
    phone: '',
    'phone-landscape': 'pl:',
    tablet: 'ta:',
    'tablet-landscape': 'tl:',
    desktop: 'de:',
    tv: 'tv:',
  }

  // Output phone value first (no prefix)
  const phoneValue = value.phone
  if (phoneValue !== undefined) {
    const formatted = formatValue(phoneValue)
    parts.push(formatted)
  }

  // Check if all breakpoints are explicitly set (not undefined)
  // If so, we should output all of them to preserve the exact structure
  const allBreakpointsExplicit = breakpoints.every((bp) => value[bp] !== undefined)

  // Output other breakpoints
  let lastValue: T | undefined = phoneValue
  for (const bp of breakpoints.slice(1)) {
    const bpValue = value[bp]
    if (bpValue !== undefined) {
      const differsFromPrevious = JSON.stringify(bpValue) !== JSON.stringify(lastValue)
      
      if (differsFromPrevious) {
        // Value differs - always output
        const formatted = formatValue(bpValue)
        parts.push(`${prefixes[bp]}${formatted}`)
        lastValue = bpValue
      } else if (allBreakpointsExplicit) {
        // Value matches previous, but all breakpoints are explicitly set
        // Output it to preserve the exact structure for round-trip accuracy
        const formatted = formatValue(bpValue)
        parts.push(`${prefixes[bp]}${formatted}`)
        // Don't update lastValue since it's the same
      }
      // Otherwise, skip this breakpoint (it matches previous and not all are explicit)
    }
  }

  return parts.length > 0 ? parts.join(' ') : undefined
}

/**
 * Format color settings back to format string
 * Converts color settings back to "solid:primary-40" or "gradient:primary-40|primary-80:to-right" format
 */
export function formatColorFormat(settings: {
  colorType?: ColorAdaptiveValue<ColorType>
  colorSolid?: ColorAdaptiveValue<ColorVariant>
  colorStartGradient?: ColorAdaptiveValue<ColorVariant>
  colorEndGradient?: ColorAdaptiveValue<ColorVariant>
  colorGradientDirection?: ColorAdaptiveValue<GradientDirection>
}): string | undefined {
  if (!settings.colorType || !settings.colorSolid) return undefined

  const typeLight = settings.colorType.light || 'solid'
  const typeDark = settings.colorType.dark || typeLight
  const solidLight = settings.colorSolid.light || 'primary-40'
  const solidDark = settings.colorSolid.dark || solidLight

  // Handle adaptive color
  if (typeLight === 'adaptive' || typeDark === 'adaptive') {
    // If both light and dark are adaptive, or if they have different colors, format as adaptive
    if (typeLight === 'adaptive' && typeDark === 'adaptive') {
      return 'adaptive'
    } else if (solidLight !== solidDark) {
      // Different colors for light/dark: format as "adaptive:color1 color2"
      return `adaptive:${solidLight} ${solidDark}`
    } else {
      return 'adaptive'
    }
  }

  // Handle solid color
  if (typeLight === 'solid' || typeDark === 'solid') {
    if (solidLight !== solidDark) {
      // Different colors for light/dark: format as "solid:color1 solid:color2"
      return `solid:${solidLight} solid:${solidDark}`
    } else {
      return `solid:${solidLight}`
    }
  }

  // Handle gradient
  if (typeLight === 'gradient' || typeDark === 'gradient') {
    const startGradientLight = settings.colorStartGradient?.light || solidLight
    const endGradientLight = settings.colorEndGradient?.light || 'primary-80'
    const directionLight = settings.colorGradientDirection?.light || 'to-right'

    const startGradientDark = settings.colorStartGradient?.dark || solidDark
    const endGradientDark = settings.colorEndGradient?.dark || 'primary-80'
    const directionDark = settings.colorGradientDirection?.dark || 'to-right'

    if (
      startGradientLight !== startGradientDark ||
      endGradientLight !== endGradientDark ||
      directionLight !== directionDark
    ) {
      // Different gradients for light/dark: format as "gradient:color1|color2:dir gradient:color3|color4:dir"
      return `gradient:${startGradientLight}|${endGradientLight}:${directionLight} gradient:${startGradientDark}|${endGradientDark}:${directionDark}`
    } else {
      return `gradient:${startGradientLight}|${endGradientLight}:${directionLight}`
    }
  }

  // Handle transparent
  if (typeLight === 'transparent' || typeDark === 'transparent') {
    return 'transparent'
  }

  // Default to solid
  if (solidLight !== solidDark) {
    return `solid:${solidLight} solid:${solidDark}`
  }
  return `solid:${solidLight}`
}

/**
 * Format color variant back to format string
 * Converts ColorAdaptiveValue<ColorVariant> back to "primary-40" or "primary-40 dark:secondary-30" format
 */
export function formatColorVariantFormat(
  colorVariant: ColorAdaptiveValue<ColorVariant> | undefined
): string | undefined {
  if (!colorVariant) return undefined

  const light = colorVariant.light || 'primary-40'
  const dark = colorVariant.dark || light

  if (light === dark) {
    return light
  }

  return `${light} dark:${dark}`
}

/**
 * Format typography settings back to format string
 * Converts ResponsiveValue<TypographyValue> back to "label-medium ta:label-large" format
 */
export function formatTypographyFormat(typography: ResponsiveValue<TypographyValue> | undefined): string | undefined {
  if (!typography) return undefined

  return formatResponsiveValue(typography, (val) => val.typography || 'label-medium')
}

/**
 * Format spacing settings back to format string
 * Converts ResponsiveValue<SpacingInputValue> back to "p:16px ta:p:32px" format
 */
export function formatSpacingFormat(spacing: ResponsiveValue<SpacingInputValue> | undefined): string | undefined {
  if (!spacing) return undefined

  // Format each breakpoint separately to ensure proper prefix handling
  const breakpoints: AppBreakpoint[] = ['phone', 'phone-landscape', 'tablet', 'tablet-landscape', 'desktop', 'tv']
  const prefixes: Record<AppBreakpoint, string> = {
    phone: '',
    'phone-landscape': 'pl:',
    tablet: 'ta:',
    'tablet-landscape': 'tl:',
    desktop: 'de:',
    tv: 'tv:',
  }

  const formatSpacingValue = (val: SpacingInputValue, prefix: string = ''): string => {
    const parts: string[] = []
    if (val.top && val.top.value !== undefined) {
      if (
        val.top.value === val.bottom?.value &&
        val.top.value === val.left?.value &&
        val.top.value === val.right?.value
      ) {
        // All same - use p:
        parts.push(`${prefix}p:${val.top.value}${val.top.unit || 'px'}`)
      } else {
        // Different values - use individual directions (all get the prefix)
        if (val.top.value !== 0 || val.top.unit !== 'px') {
          parts.push(`${prefix}pt:${val.top.value}${val.top.unit || 'px'}`)
        }
        if (val.right && val.right.value !== undefined && (val.right.value !== 0 || val.right.unit !== 'px')) {
          parts.push(`${prefix}pr:${val.right.value}${val.right.unit || 'px'}`)
        }
        if (val.bottom && val.bottom.value !== undefined && (val.bottom.value !== 0 || val.bottom.unit !== 'px')) {
          parts.push(`${prefix}pb:${val.bottom.value}${val.bottom.unit || 'px'}`)
        }
        if (val.left && val.left.value !== undefined && (val.left.value !== 0 || val.left.unit !== 'px')) {
          parts.push(`${prefix}pl:${val.left.value}${val.left.unit || 'px'}`)
        }
      }
    }
    return parts.join(' ')
  }

  // Check if all breakpoints have the same spacing
  const phoneSpacing = spacing.phone
  const allSame = breakpoints.every((bp) => {
    const bpSpacing = spacing[bp]
    if (!bpSpacing || !phoneSpacing) return false
    return (
      bpSpacing.top?.value === phoneSpacing.top?.value &&
      bpSpacing.right?.value === phoneSpacing.right?.value &&
      bpSpacing.bottom?.value === phoneSpacing.bottom?.value &&
      bpSpacing.left?.value === phoneSpacing.left?.value &&
      bpSpacing.top?.unit === phoneSpacing.top?.unit &&
      bpSpacing.right?.unit === phoneSpacing.right?.unit &&
      bpSpacing.bottom?.unit === phoneSpacing.bottom?.unit &&
      bpSpacing.left?.unit === phoneSpacing.left?.unit
    )
  })

  if (allSame && phoneSpacing) {
    return formatSpacingValue(phoneSpacing)
  }

  // Format each breakpoint with its prefix
  const parts: string[] = []
  for (const bp of breakpoints) {
    const bpSpacing = spacing[bp]
    if (bpSpacing) {
      const prefix = prefixes[bp]
      const formatted = formatSpacingValue(bpSpacing, prefix)
      if (formatted) {
        parts.push(formatted)
      }
    }
  }

  return parts.length > 0 ? parts.join(' ') : undefined
}

/**
 * Format text alignment settings back to format string
 * Converts ResponsiveValue<TextAlignment> back to "t:center ta:t:left" format
 * TextAlignment: 'left' | 'center' | 'right' | 'justify'
 */
export function formatTextAlignmentFormat(alignment: ResponsiveValue<TextAlignment> | undefined): string | undefined {
  if (!alignment) return undefined

  return formatResponsiveValue(alignment, (val) => {
    // Map TextAlignment to text alignment format (t:)
    // Handle "start" and "end" for backward compatibility (map to left/right)
    if (val === 'left' || (val as any) === 'start') return 't:left'
    if (val === 'right' || (val as any) === 'end') return 't:right'
    if (val === 'justify') return 't:justify'
    return 't:center' // default fallback
  })
}

/**
 * Format component alignment settings back to format string
 * Converts ResponsiveValue<ComponentAlignment> back to "c:center ta:c:start" format
 * ComponentAlignment: 'start' | 'center' | 'end' | 'parent'
 */
export function formatComponentAlignmentFormat(
  alignment: ResponsiveValue<ComponentAlignment> | undefined
): string | undefined {
  if (!alignment) return undefined

  return formatResponsiveValue(alignment, (val) => {
    // Map ComponentAlignment to component alignment format (c:)
    if (val === 'parent') return 'c:parent'
    if (val === 'start') return 'c:start'
    if (val === 'end') return 'c:end'
    return 'c:center' // default fallback
  })
}

/**
 * Format horizontal alignment settings back to format string
 * Converts ResponsiveValue<HorizontalAlignment> back to "h:center ta:h:start" format
 * HorizontalAlignment: 'start' | 'center' | 'end' | 'parent'
 */
export function formatHorizontalAlignmentFormat(
  alignment: ResponsiveValue<HorizontalAlignment> | undefined
): string | undefined {
  if (!alignment) return undefined

  return formatResponsiveValue(alignment, (val) => {
    // Map HorizontalAlignment to horizontal alignment format (h:)
    if (val === 'parent') return 'h:parent'
    if (val === 'start') return 'h:start'
    if (val === 'end') return 'h:end'
    return 'h:center' // default fallback
  })
}

/**
 * Format vertical alignment settings back to format string
 * Converts ResponsiveValue<VerticalAlignment> back to "v:center ta:v:start" format
 * VerticalAlignment: 'start' | 'center' | 'end'
 */
export function formatVerticalAlignmentFormat(
  alignment: ResponsiveValue<VerticalAlignment> | undefined
): string | undefined {
  if (!alignment) return undefined
  return formatResponsiveValue(alignment, (val) => {
    if (val === 'start') return 'v:start'
    if (val === 'end') return 'v:end'
    return 'v:center' // default fallback
  })
}

/**
 * Format alignment settings back to format string (legacy - for backward compatibility)
 * Converts ResponsiveValue<TextAlignment> back to "h:center ta:h:start" format
 * @deprecated Use formatTextAlignmentFormat or formatComponentAlignmentFormat instead
 */
export function formatAlignmentFormat(alignment: ResponsiveValue<TextAlignment> | undefined): string | undefined {
  // Legacy function - maps TextAlignment to component alignment format for backward compatibility
  return formatTextAlignmentFormat(alignment)?.replace(/^t:/, 'h:')
}

/**
 * Format FAB alignment settings back to format string
 * Converts ResponsiveValue<string> (with "start", "center", "end" values) back to "h:end ta:h:center" format
 */
export function formatFabAlignmentFormat(alignment: ResponsiveValue<string> | undefined): string | undefined {
  if (!alignment) return undefined

  return formatResponsiveValue(alignment, (val) => {
    // Map ComponentAlignment values to format string
    if (val === 'start') return 'h:start'
    if (val === 'end') return 'h:end'
    return 'h:center'
  })
}

/**
 * Format width settings back to format string
 * Converts ResponsiveValue<string[] | number[]> back to "w:100% de:w:800px" format
 */
export function formatWidthFormat(width: ResponsiveValue<string[] | number[]> | undefined): string | undefined {
  if (!width) return undefined

  return formatResponsiveValue(width, (val) => {
    if (Array.isArray(val) && val.length > 0) {
      const firstVal = val[0]
      if (typeof firstVal === 'string') {
        return `w:${firstVal}`
      } else {
        // Numbers without units are treated as percentages (e.g., 100 = 100%)
        return `w:${firstVal}%`
      }
    }
    return 'w:100%'
  })
}

/**
 * Format corner radius (shape) settings back to format string
 * Valid values: none, extra-small, small, medium, large, extra-large, full
 * Returns the value as-is for all valid corner radius values
 */
export function formatCornerRadiusFormat(cornerRadius: string | undefined): string | undefined {
  if (!cornerRadius) return undefined

  // Return the value as-is for all valid values
  // Valid values according to docs: none, extra-small, small, medium, large, extra-large, full
  return cornerRadius
}

/**
 * Format responsive corner radius (shape) settings back to format string
 * Converts ResponsiveValue<T> back to "drop-br ta:circle" format
 */
export function formatResponsiveCornerRadiusFormat<T extends string>(
  shape: ResponsiveValue<T> | undefined
): string | undefined {
  if (!shape) return undefined

  return formatResponsiveValue(shape, (val) => val as string)
}

/**
 * Format aspect ratio settings back to format string
 * Converts ResponsiveValue<AiAspectRatio> back to "16-9" or "1-1 ta:16-9" format
 * Note: Converts colon format to dash format (e.g., "1:1" -> "1-1")
 */
export function formatAspectRatioFormat(aspectRatio: ResponsiveValue<string> | undefined): string | undefined {
  if (!aspectRatio) return undefined

  return formatResponsiveValue(aspectRatio, (val) => {
    // Convert colon format to dash format (e.g., "1:1" -> "1-1", "16:9" -> "16-9")
    return val.replace(/:/g, '-')
  })
}

/**
 * Format orientation value back to format string
 * Converts "vertical"/"horizontal" back to "v"/"h" format
 */
export function formatOrientationFormat(value: ResponsiveValue<string> | undefined): string | undefined {
  return formatResponsiveValue(value, (val) => {
    if (val === 'horizontal') return 'h'
    if (val === 'vertical') return 'v'
    return val === 'h' ? 'h' : 'v' // Default to 'v' if unknown
  })
}

/**
 * Format layout value back to format string
 * Converts [12] or [6,6] back to "cols:100" or "cols:50:50" format
 */
export function formatLayoutFormat(value: ResponsiveValue<number[]> | undefined): string | undefined {
  return formatResponsiveValue(value, (val) => {
    if (!Array.isArray(val) || val.length === 0) return 'cols:100'
    // Convert 1/12 parts back to percentages
    const percentages = val.map((w) => Math.round((w / 12) * 100))
    return `cols:${percentages.join(':')}`
  })
}

/**
 * Format gap value back to format string
 * Converts {value, unit} back to "gap:10px" format
 */
export function formatGapFormat(
  value: ResponsiveValue<{ value: number; unit: string }> | undefined
): string | undefined {
  return formatResponsiveValue(value, (val) => {
    if (!val || typeof val.value !== 'number') return 'gap:0px'
    return `gap:${val.value}${val.unit || 'px'}`
  })
}

/**
 * Format limit settings back to limit format string
 * Converts LimitSettings back to "limit:10 en:true max:100" format
 */
export function formatLimitFormat(
  limit: { enabled: boolean; limit: number; value: number } | undefined
): string | number | undefined {
  if (!limit) return undefined

  // If enabled is true and limit matches defaults, return simple number
  if (limit.enabled && limit.limit === 100) {
    return limit.value
  }

  // Build compact format string
  const parts: string[] = [`limit:${limit.value}`]
  if (limit.enabled !== false) {
    parts.push(`en:${limit.enabled}`)
  }
  if (limit.limit !== 100) {
    parts.push(`max:${limit.limit}`)
  }

  return parts.join(' ')
}

/**
 * Format table data source back to simplified format
 * Converts TableDataSource back to string (table ID) or simplified object format
 */
export function formatTableDataSource(
  dataSource: { table: string; columns: any[]; isDetailPageTable?: boolean } | undefined
): string | any | undefined {
  if (!dataSource) return undefined

  const { table, columns, isDetailPageTable } = dataSource

  // If no columns or empty columns, return just table ID as string
  if (!columns || columns.length === 0) {
    return table
  }

  // Format columns to simplified format
  const formattedColumns = columns.map((col) => {
    // Build compact format: "col:Cf35zy lbl:ID ic:star us:false vs:true"
    const parts: string[] = [`col:${col.col || col.id || ''}`]

    if (col.label && col.label !== col.col && col.label !== col.id) {
      parts.push(`lbl:${col.label}`)
    }

    if (col.icon) {
      parts.push(`ic:${col.icon}`)
    }

    if (col.userSpecific) {
      parts.push(`us:${col.userSpecific}`)
    }

    // Format visibility if not all true
    if (col.visibility) {
      const allVisible = Object.values(col.visibility).every((v) => v === true)
      const allHidden = Object.values(col.visibility).every((v) => v === false)

      if (!allVisible && !allHidden) {
        // Responsive visibility - format as "vs:ph:true ta:false de:true"
        const visParts: string[] = []
        const bpMap: Record<string, string> = {
          phone: 'ph',
          'phone-landscape': 'pl',
          tablet: 'ta',
          'tablet-landscape': 'tl',
          desktop: 'de',
          tv: 'tv',
        }
        for (const [bp, prefix] of Object.entries(bpMap)) {
          if (col.visibility[bp as keyof typeof col.visibility] !== undefined) {
            visParts.push(`${prefix}:${col.visibility[bp as keyof typeof col.visibility]}`)
          }
        }
        if (visParts.length > 0) {
          parts.push(`vs:${visParts.join(' ')}`)
        }
      } else if (!allVisible) {
        parts.push('vs:false')
      }
      // If allVisible, we skip vs: (defaults to true)
    }

    return parts.join(' ')
  })

  // Build simplified object
  const result: any = {
    tableId: table,
    columns: formattedColumns,
  }

  if (isDetailPageTable) {
    result.isDetailPageTable = isDetailPageTable
  }

  return result
}
